import { ReactNode } from "react";

/**
 * BookLeaf — a single bound page.
 *
 * Renders warm paper with a binding gutter on the left, a stack of page-edges
 * behind it (so the book has thickness), a dated folio up top, and a page
 * number at the foot. Used for both the prologue and the reader's own pages.
 */
export default function BookLeaf({
  folio,
  pageLabel,
  children,
  footer,
}: {
  folio?: string;
  pageLabel?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="relative w-full sm:w-[92vw] md:w-[680px]">
      {/* Stacked page-edges behind the leaf — the book's thickness */}
      <div className="absolute inset-0 translate-x-[9px] translate-y-[9px] rounded-r-[14px] rounded-l-[4px] bg-[#e9dfcc] border border-[#d8ccb4]" />
      <div className="absolute inset-0 translate-x-[4.5px] translate-y-[4.5px] rounded-r-[14px] rounded-l-[4px] bg-[#f1e9d8] border border-[#e0d5c0]" />

      {/* The page itself */}
      <div
        className="relative rounded-r-[14px] rounded-l-[4px] border border-[#ddd0b8] bg-[#fdfbf5] flex flex-col h-[74vh] overflow-hidden pl-8 pr-6 sm:pl-12 sm:pr-10 pt-7 pb-6"
        style={{
          boxShadow:
            "inset 18px 0 32px -24px rgba(76,63,45,0.55), 0 22px 50px -24px rgba(60,50,35,0.42)",
        }}
      >
        {/* Binding line near the gutter */}
        <div className="pointer-events-none absolute left-[13px] top-5 bottom-5 w-px bg-[#e7ddc7]" />

        {/* Top folio — the date, quietly */}
        {folio && (
          <div className="text-center mb-5 shrink-0">
            <p className="text-[0.68rem] uppercase tracking-[0.28em] text-[#a99f89]">
              {folio}
            </p>
            <div className="mx-auto mt-2 h-px w-14 bg-[#e2d8c3]" />
          </div>
        )}

        {/* The page's body */}
        <div className="flex-1 overflow-y-auto scrollbar-hide">{children}</div>

        {/* Footer controls */}
        {footer && <div className="pt-4 shrink-0">{footer}</div>}

        {/* Page number */}
        {pageLabel && (
          <p className="text-center mt-3 shrink-0 text-[0.72rem] italic tracking-wide text-[#b4a98f] font-serif">
            {pageLabel}
          </p>
        )}
      </div>
    </div>
  );
}
